import { inject, Service, provideAppInitializer } from '@angular/core';
import { catchError, forkJoin, Observable, of, tap } from 'rxjs';
import { Feature } from '../../model/features.enum';
import { PortalId } from '../../model/portal-id.enum';
import { UserDetails } from '../../model/user.model';
import { FeatureToggleService } from '../feature-toggle/feature-toggle.service';
import { WebConfigService } from '../web-config/web-config.service';
import { AuthStateService } from './auth-state.service';
import { WINDOW } from '../../providers/window/window.provider';
import { AuthService } from '../auth.service';

@Service()
export class AuthStateProvider {
  #authState = inject(AuthStateService);
  #webConfig = inject(WebConfigService);
  #authService = inject(AuthService);
  #window = inject(WINDOW);
  #featureToggle = inject(FeatureToggleService);

  fetchDetails(): Observable<{ user: UserDetails } | null> {
    const hasConfig = !!this.#webConfig?.env;
    const isWebBackend = this.#featureToggle.when(Feature.WebBackend)();
    const isLinkApp = this.#window.location.href.includes('link');
    if (!isWebBackend) {
      this.#authState.removeBackendCredentials();
    }
    if (!hasConfig || isLinkApp) {
      return of(null);
    }

    const userObservable$ = this.#authState.getUserDetails();
    const user$: Observable<UserDetails> = userObservable$.pipe(
      catchError(() => {
        this.#authState.removeBackendCredentials();
        return of(null);
      }),
    );
    switch (this.#webConfig.portalId) {
      case PortalId.Merchant:
        if (this.#authService.isLoggedIn) {
          this.#authState.updateMerchantDetails();
        }
        return forkJoin({ user: user$ });
      case PortalId.Developer:
        return forkJoin({ user: user$ }).pipe(tap(() => this.#authState.updateEntityDetails()));
      case PortalId.Support:
        this.#authState.updateMerchantDetails();
        return forkJoin({ user: user$ });
      default:
        this.#authState.updateMerchantDetails();
        return forkJoin({ user: user$ });
    }
  }
}

export const AuthStateProviderInitializer = provideAppInitializer(() => {
  return inject(AuthStateProvider).fetchDetails();
});
